import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Crown,
  Building2,
  Users,
  CalendarCheck,
  Wallet,
  TrendingUp,
  Loader2,
  Lock,
  ArrowRight,
  Landmark,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_LABEL = {
  free: { label: "Gratuito", color: "text-slate-400 bg-slate-800/70" },
  pro: { label: "Prata", color: "text-[#D4AF37] bg-[#D4AF37]/15" },
  grande_oriente: { label: "Ouro", color: "text-purple-300 bg-purple-900/40" },
};

const formatBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const GrandOrientDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/grand-orient/overview`, { withCredentials: true });
        setData(res.data);
      } catch (err) {
        if (err.response?.status === 403) {
          setForbidden(true);
        } else if (err.response?.status === 401) {
          toast.error("Faça login para acessar.");
        } else {
          toast.error("Erro ao carregar visão consolidada.");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <div data-testid="grand-orient-forbidden" className="max-w-2xl mx-auto py-16 text-center">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-purple-900/30 border border-purple-700/60 flex items-center justify-center">
          <Lock className="w-8 h-8 text-purple-300" />
        </div>
        <h2 className="mt-6 text-3xl font-serif font-bold text-white">
          Dashboard do Plano Ouro
        </h2>
        <p className="mt-4 text-slate-400 max-w-lg mx-auto leading-relaxed">
          Este é um recurso exclusivo do plano <strong className="text-purple-300">Ouro</strong>.
          Com ele, Potências e Federações gerenciam todas as Lojas com um dashboard consolidado, KPIs
          agregados e visão financeira do sistema.
        </p>
        <a
          data-testid="grand-orient-upgrade-cta"
          href="/pricing"
          className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-purple-700 hover:bg-purple-600 text-white font-bold uppercase tracking-wider text-sm rounded-xl transition-all"
        >
          Fazer upgrade <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    );
  }

  if (!data) return null;

  const { totals, plan_distribution, lojas } = data;
  const filtered = query.trim()
    ? lojas.filter(
        (l) =>
          l.name?.toLowerCase().includes(query.toLowerCase()) ||
          String(l.number || "").includes(query) ||
          l.oriente?.toLowerCase().includes(query.toLowerCase())
      )
    : lojas;

  return (
    <div data-testid="grand-orient-dashboard" className="space-y-8">
      {/* Header */}
      <div className="luxury-card p-6 border-l-4 border-l-purple-500 bg-gradient-to-r from-purple-950/30 to-transparent">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-purple-300 font-bold">
              <Crown className="w-4 h-4" />
              Plano Ouro
            </div>
            <h1 className="mt-2 text-3xl font-serif font-bold text-white">
              Visão Consolidada do Plano Ouro
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Todas as Lojas gerenciadas pela sua Potência em um único painel.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Última sincronização</div>
            <div className="text-sm text-slate-300 font-mono mt-1">{new Date().toLocaleString("pt-BR")}</div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          testid="kpi-lojas"
          icon={Building2}
          label="Lojas ativas"
          value={totals.lojas}
          accent="text-purple-300 bg-purple-900/30"
        />
        <KPI
          testid="kpi-members"
          icon={Users}
          label="Obreiros no sistema"
          value={totals.members}
          accent="text-[#D4AF37] bg-[#D4AF37]/15"
        />
        <KPI
          testid="kpi-sessions"
          icon={CalendarCheck}
          label="Sessões registradas"
          value={totals.sessions}
          accent="text-emerald-300 bg-emerald-900/30"
        />
        <KPI
          testid="kpi-balance"
          icon={Wallet}
          label="Saldo consolidado"
          value={formatBRL(totals.balance)}
          accent="text-cyan-300 bg-cyan-900/30"
        />
      </div>

      {/* Plan distribution */}
      <div className="luxury-card p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 font-bold mb-4">
          <TrendingUp className="w-3.5 h-3.5" />
          Distribuição por plano
        </div>
        <div className="grid grid-cols-3 gap-3">
          {["free", "pro", "grande_oriente"].map((p) => {
            const cfg = PLAN_LABEL[p];
            const count = plan_distribution[p] || 0;
            const pct = totals.lojas ? Math.round((count / totals.lojas) * 100) : 0;
            return (
              <div
                key={p}
                data-testid={`plan-dist-${p}`}
                className="rounded-xl bg-slate-900/40 border border-slate-800 p-4"
              >
                <div className={`inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${cfg.color}`}>
                  {cfg.label}
                </div>
                <div className="mt-3 text-2xl font-serif font-bold text-white">{count}</div>
                <div className="text-xs text-slate-500">{pct}% das Lojas</div>
                <div className="mt-2 w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#D4AF37] rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <input
          data-testid="grand-orient-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nome, número ou Oriente…"
          className="flex-1 px-4 py-2.5 rounded-xl bg-slate-900/60 border border-slate-800 focus:border-[#D4AF37] outline-none text-sm text-slate-100"
        />
        <span className="text-xs text-slate-500 font-mono">
          {filtered.length} de {lojas.length}
        </span>
      </div>

      {/* Lojas table */}
      <div className="luxury-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800 bg-slate-900/40">
                <th className="text-left px-4 py-3">Loja</th>
                <th className="text-left px-4 py-3">Oriente</th>
                <th className="text-left px-4 py-3">Plano</th>
                <th className="text-right px-4 py-3">Obreiros</th>
                <th className="text-right px-4 py-3">Sessões</th>
                <th className="text-right px-4 py-3">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const cfg = PLAN_LABEL[l.plan_id] || PLAN_LABEL.free;
                return (
                  <tr
                    key={l.id}
                    data-testid={`loja-row-${l.id}`}
                    className="border-b border-slate-800/60 hover:bg-slate-900/40 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {l.logo_b64 ? (
                          <img src={l.logo_b64} alt="" className="w-8 h-8 rounded object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-slate-800 flex items-center justify-center">
                            <Landmark className="w-4 h-4 text-[#D4AF37]" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-white">{l.name}</div>
                          <div className="text-[10px] text-slate-500 font-mono">nº {l.number || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{l.oriente || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${cfg.color}`}>
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-100">{l.members_count}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-100">{l.sessions_count}</td>
                    <td
                      className={`px-4 py-3 text-right font-mono font-semibold ${
                        l.balance >= 0 ? "text-emerald-300" : "text-red-300"
                      }`}
                    >
                      {formatBRL(l.balance)}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">
                    Nenhuma Loja encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const KPI = ({ testid, icon: Icon, label, value, accent }) => (
  <div data-testid={testid} className="luxury-card p-5">
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="mt-3 text-2xl font-serif font-bold text-white">{value}</div>
    <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
  </div>
);

export default GrandOrientDashboard;
